import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackAppliedSearchTerm } from '@air/analytics';
import { type Clip } from '@air/types';

import { QueryParamNames } from '~/constants/search';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import {
  clearSimilarToFilterAction,
  clearSimilarToImageFilterValueAction,
  setSimilarToFilterAction,
  setSimilarToImageFilterValueAction,
} from '~/store/filters/actions';
import { similarToFilterSelector, similarToImageFilterSelector } from '~/store/filters/selectors';
import { getPathWithOmittedParams } from '~/utils/PathUtils';

type Source = 'drag-and-drop' | 'search-command';
const SOURCE_TERM_MAP: {
  [key in Source]: string;
} = {
  'drag-and-drop': 'similar_to_image_drag',
  'search-command': 'similar_to_image',
};

export const usePrivateSmartSearchSimilarToFilter = () => {
  const { trackAppliedSearchTerm } = useTrackAppliedSearchTerm();
  const { canUseSmartSearch } = usePrivateSearchContext();
  const { removeFilters, replaceFilters } = useApplyFilters();
  const similarToImage = useSelector(similarToImageFilterSelector);
  const similarTo = useSelector(similarToFilterSelector);

  const dispatch = useDispatch();

  const addSimilarToImageFilter = useCallback(
    (base64Image: string, source: Source) => {
      if (!canUseSmartSearch) {
        return;
      }

      trackAppliedSearchTerm({
        term: SOURCE_TERM_MAP[source],
        search_type: 'image',
      });

      // Remove search param from URL and apply empty filters in a single history entry
      replaceFilters({
        url: getPathWithOmittedParams(Router.asPath, [QueryParamNames.Search]),
        filters: {},
      }).then(() => {
        // Store base64 image in Redux only after URL update - not in URL to avoid large base64 strings
        dispatch(
          setSimilarToImageFilterValueAction({
            similarToImage: base64Image,
          }),
        );
      });
    },
    [dispatch, canUseSmartSearch, replaceFilters, trackAppliedSearchTerm],
  );

  const addSimilarToFilter = useCallback(
    (clip: Clip) => {
      if (!canUseSmartSearch) {
        return;
      }

      trackAppliedSearchTerm({
        term: 'similar_air_image_search_drag',
        search_type: 'image',
      });

      // Remove search param from URL and apply similarTo filter in a single history entry
      replaceFilters({
        url: getPathWithOmittedParams(Router.asPath, [QueryParamNames.Search]),
        filters: {
          similarTo: clip.id,
        },
      }).then(() => {
        // Dispatch to Redux after URL update to maintain consistency with FiltersProvider sync
        dispatch(
          setSimilarToFilterAction({
            similarTo: clip.id,
          }),
        );
      });
    },
    [dispatch, replaceFilters, canUseSmartSearch, trackAppliedSearchTerm],
  );

  const removeSimilarToImageFilter = useCallback(async () => {
    if (similarToImage) {
      await removeFilters({ filters: [{ type: 'similarToImage' }] });
      dispatch(clearSimilarToImageFilterValueAction());
    }
  }, [dispatch, removeFilters, similarToImage]);

  const removeSimilarToFilter = useCallback(async () => {
    if (similarTo) {
      await removeFilters({ filters: [{ type: 'similarTo' }] });
      dispatch(clearSimilarToFilterAction());
    }
  }, [dispatch, removeFilters, similarTo]);

  return {
    addSimilarToImageFilter,
    addSimilarToFilter,
    removeSimilarToImageFilter,
    removeSimilarToFilter,
  };
};
